<template>
    <VModalWrapper :name="name" :title="title" v-on="listeners" v-bind="attributes">
        <div class="batch-form" v-if="common">
            <!-- ОБЩИЕ ПОЛЯ — ОДИН РАЗ НА ВСЮ ПАРТИЮ. Сорок ноутбуков из одной накладной
                 отличаются только серийным номером; заставлять заполнять город и цену
                 сорок раз — это и есть та работа, которую партия убирает.

                 ЗАГОЛОВКА У ОДНОЙ ЕДИНИЦЫ НЕТ. «Общее для всех 1 единиц» — это не просто
                 неграмотная строка: она обещает партию там, где её нет, и заставляет
                 искать, с чем же общее. Форма одна, а показывает себя по-разному —
                 см. isBatch. -->
            <section class="batch-form__section">
                <h3 class="batch-form__section-title" v-if="isBatch">Общее для всех {{ count }} единиц</h3>

                <div class="batch-form__grid">
                    <div class="batch-form__field">
                        <label class="active-form__label active-form__label_required" for="batch-name">
                            Наименование / модель
                        </label>

                        <input
                            id="batch-name"
                            class="active-form__control control"
                            type="text"
                            placeholder="Ноутбук Huawei MateBook D 16"
                            v-model="common.name"
                        />

                        <span class="batch-form__error" v-if="fieldErrors.name">{{ fieldErrors.name }}</span>
                    </div>

                    <div class="batch-form__field">
                        <label class="active-form__label active-form__label_required" for="batch-city">Город</label>

                        <VSelect
                            id="batch-city"
                            class="active-form__control control"
                            label="name"
                            placeholder="Выберите город"
                            :clearable="false"
                            :options="cityOptions"
                            :reduce="(option) => option.id"
                            v-model="common.city_id"
                        />

                        <span class="batch-form__error" v-if="fieldErrors.city_id">{{ fieldErrors.city_id }}</span>
                    </div>

                    <div class="batch-form__field">
                        <label class="active-form__label active-form__label_required" for="batch-legal-entity">
                            Юр. лицо
                        </label>

                        <VSelect
                            id="batch-legal-entity"
                            class="active-form__control control"
                            label="name"
                            :placeholder="legalEntityOptions.length ? 'Выберите юр. лицо' : 'Справочник пуст'"
                            :disabled="!legalEntityOptions.length"
                            :options="legalEntityOptions"
                            :reduce="(option) => option.id"
                            v-model="common.legal_entity_id"
                        />

                        <span class="batch-form__error" v-if="fieldErrors.legal_entity_id">{{
                            fieldErrors.legal_entity_id
                        }}</span>
                    </div>

                    <div class="batch-form__field">
                        <label class="active-form__label active-form__label_required" for="batch-cost">
                            {{ isBatch ? 'Стоимость за единицу' : 'Стоимость' }}
                        </label>

                        <input
                            id="batch-cost"
                            class="active-form__control control"
                            type="number"
                            inputmode="decimal"
                            min="0"
                            step="0.01"
                            v-model.number="common.cost"
                        />

                        <span class="batch-form__error" v-if="fieldErrors.cost">{{ fieldErrors.cost }}</span>
                    </div>

                    <div class="batch-form__field">
                        <label class="active-form__label" for="batch-delivered">Дата поставки</label>

                        <input
                            id="batch-delivered"
                            class="active-form__control control"
                            type="date"
                            v-model="common.delivered_at"
                        />

                        <span class="batch-form__error" v-if="fieldErrors.delivered_at">{{
                            fieldErrors.delivered_at
                        }}</span>
                    </div>

                    <!-- ГАРАНТИЯ В МЕСЯЦАХ. В накладной написано «12 месяцев», а не
                         «до 17.06.2027»; дату считает сервер от даты поставки, здесь
                         она показывается заранее, чтобы человек себя проверил. -->
                    <div class="batch-form__field">
                        <label class="active-form__label" for="batch-warranty">Гарантия, месяцев</label>

                        <input
                            id="batch-warranty"
                            class="active-form__control control"
                            type="number"
                            inputmode="numeric"
                            min="0"
                            max="120"
                            v-model.number="common.warranty_months"
                        />

                        <span class="batch-form__error" v-if="fieldErrors.warranty_months">{{
                            fieldErrors.warranty_months
                        }}</span>

                        <span class="batch-form__hint" v-if="warrantyLabel">{{ warrantyLabel }}</span>
                    </div>

                    <div class="batch-form__field">
                        <label class="active-form__label" for="batch-status">Статус</label>

                        <VSelect
                            id="batch-status"
                            class="active-form__control control"
                            label="label"
                            :clearable="false"
                            :options="statusOptions"
                            :reduce="(option) => option.value"
                            v-model="common.status"
                        />

                        <span class="batch-form__error" v-if="fieldErrors.status">{{ fieldErrors.status }}</span>
                    </div>

                    <!-- ВЛАДЕЛЕЦ. Три сценария, и от выбора зависит, какое поле появится
                         следом: сотрудник, держатель или ничего. Правило одно и то же
                         при одиночном и массовом создании — оно живёт
                         в js/inventoryTemplate.js, а не в шаблоне формы. -->
                    <div class="batch-form__field">
                        <label class="active-form__label" for="batch-owner">Владелец</label>

                        <VSelect
                            id="batch-owner"
                            class="active-form__control control"
                            label="label"
                            :clearable="false"
                            :options="ownerOptions"
                            :reduce="(option) => option.value"
                            v-model="common.owner_type"
                        />
                    </div>

                    <div class="batch-form__field" v-if="ownerFields.employee">
                        <label class="active-form__label" for="batch-employee">Сотрудник</label>

                        <VSelect
                            id="batch-employee"
                            class="active-form__control control"
                            label="displayName"
                            placeholder="Выберите сотрудника"
                            :options="userOptions"
                            :reduce="(option) => option.id"
                            v-model="common.responsible_person_id"
                        />

                        <span class="batch-form__error" v-if="fieldErrors.responsible_person_id">{{
                            fieldErrors.responsible_person_id
                        }}</span>
                    </div>

                    <div class="batch-form__field" v-if="ownerFields.holder">
                        <label class="active-form__label active-form__label_required" for="batch-holder">
                            Держатель
                        </label>

                        <VSelect
                            id="batch-holder"
                            class="active-form__control control"
                            label="full_name"
                            placeholder="Выберите держателя"
                            :options="holderOptions"
                            :reduce="(option) => option.id"
                            v-model="common.holder_id"
                        />

                        <span class="batch-form__error" v-if="fieldErrors.holder_id">{{ fieldErrors.holder_id }}</span>
                    </div>

                    <!-- ПЛАНОВЫЕ СРОКИ. Только тем статусам, которым они положены (plannedDateFields):
                         по ним работают фильтры «просрочен резерв» и «просрочен возврат». -->
                    <div class="batch-form__field" v-if="dateFields.expected">
                        <label class="active-form__label" for="batch-expected">Владеет с (план)</label>

                        <input
                            id="batch-expected"
                            class="active-form__control control"
                            type="date"
                            v-model="common.holder_expected_at"
                        />
                    </div>

                    <div class="batch-form__field" v-if="dateFields.until">
                        <label class="active-form__label" for="batch-until">Вернуть до</label>

                        <input
                            id="batch-until"
                            class="active-form__control control"
                            type="date"
                            v-model="common.holder_until_at"
                        />

                        <span class="batch-form__error" v-if="fieldErrors.holder_until_at">{{
                            fieldErrors.holder_until_at
                        }}</span>
                    </div>
                </div>
            </section>

            <!-- ПОЛЯ ИЗ ШАБЛОНА. До 7-D2 атрибуты шаблона копировались в каждую единицу
                 молча: человек их не видел и поправить не мог. Теперь каждое включённое
                 поле шаблона — обычное поле формы со значением по умолчанию; обязательность
                 задаёт шаблон, а не форма. Секция не рисуется, если полей нет вовсе —
                 пустой заголовок обещал бы то, чего нет. -->
            <section class="batch-form__section" v-if="templateFields.length">
                <h3 class="batch-form__section-title">Поля шаблона</h3>

                <div class="batch-form__grid">
                    <div class="batch-form__field" v-for="(field, index) in templateFields" :key="field.type">
                        <label
                            class="active-form__label"
                            :class="{ 'active-form__label_required': field.required }"
                            :for="`batch-field-${index}`"
                        >
                            {{ field.type }}
                        </label>

                        <input
                            :id="`batch-field-${index}`"
                            class="active-form__control control"
                            type="text"
                            :placeholder="field.type"
                            v-model="field.value"
                        />

                        <span class="batch-form__error" v-if="templateFieldErrors[index]">
                            {{ templateFieldErrors[index] }}
                        </span>
                    </div>
                </div>
            </section>

            <!-- СЕРИЙНЫЕ НОМЕРА: по одному полю на единицу, и все обязательны.
                 Вставки списком здесь больше нет (7-D1): она распределяла буфер обмена
                 по чужим строкам, и её поведение приходилось объяснять — лишнее,
                 нехватка, «разнести». Обычная вставка в конкретное поле работает как
                 в любом поле ввода и ничего вокруг не трогает. -->
            <section class="batch-form__section">
                <h3 class="batch-form__section-title">{{ serialSectionTitle }}</h3>

                <!-- СТРОКИ, А НЕ ТАБЛИЦА. Партию заводят с телефона у стеллажа, и широкая
                     таблица там не помещается: каждая единица — своя строка, которая
                     на узком экране занимает всю ширину.

                     НУМЕРАЦИИ У ЕДИНСТВЕННОЙ ЕДИНИЦЫ НЕТ: «1» рядом с одним полем
                     не различает ничего и только обещает, что где-то есть вторая. -->
                <ul class="batch-form__units">
                    <li class="batch-form__unit" v-for="(unit, index) in units" :key="index">
                        <span class="batch-form__unit-number" v-if="isBatch">{{ index + 1 }}</span>

                        <div class="batch-form__unit-field">
                            <input
                                class="active-form__control control batch-form__serial"
                                :class="{
                                    'batch-form__serial_empty': serialState(index) === 'empty',
                                    'batch-form__serial_duplicate': serialState(index) === 'duplicate',
                                }"
                                type="text"
                                autocapitalize="characters"
                                autocomplete="off"
                                spellcheck="false"
                                :placeholder="serialPlaceholder(index)"
                                :aria-label="serialPlaceholder(index)"
                                v-model="unit.serial"
                            />

                            <span class="batch-form__serial-note" v-if="unitErrors[index]">
                                {{ unitErrors[index] }}
                            </span>
                        </div>
                    </li>
                </ul>

                <!-- «Заполнено 3 из 5» — счётчик партии. У одной единицы считать нечего:
                     поле либо заполнено, либо под ним стоит ошибка. -->
                <p class="batch-form__hint" v-if="isBatch">{{ serialSummary }}</p>
            </section>

            <!-- ОБЩИЙ БЛОК ОСТАЛСЯ ТОЛЬКО ДЛЯ НЕПРИВЯЗЫВАЕМОГО: количество единиц задаёт
                 предыдущий шаг, и поля для него здесь нет; отказ сервера по ключу, которому
                 не нашлось поля, терять тоже нельзя. Всё остальное стоит под своим полем.
                 Сеть, права и системные отказы сюда не попадают вовсе — их показывает
                 общая модалка ошибки (см. submit). -->
            <p class="batch-form__reason" v-for="reason in commonErrors" :key="reason">{{ reason }}</p>

            <div class="batch-form__actions">
                <VButton type="button" class="button_green-light" :disabled="isSaving" @click="hide"> Отмена </VButton>

                <!-- КНОПКА НЕ ГАСНЕТ. Погашенная кнопка не говорит, чего не хватает,
                     и человек ищет это перебором по десяти полям. Теперь нажатие
                     показывает ошибки там, где их исправляют. -->
                <VButton type="button" class="button_green" :disabled="isSaving" @click="submit">
                    {{ isSaving ? 'Создаём…' : createLabel }}
                </VButton>
            </div>
        </div>
    </VModalWrapper>
</template>
<script>
// Components
import VButton from '../../../components/VButton/VButton.vue';
import VSelect from '../../../components/VSelect/VSelect.vue';
import VModalWrapper from '../../../components/VModalWrapper/VModalWrapper.vue';

// Other
import Component from 'vue-class-component';
import { Prop } from 'vue-property-decorator';
import ModalNames from '../../../../js/enums/ModalNames';
import ModalMixin from '../../../../js/vue/mixins/ModalMixin';
import InventoryItemStatusTitle from '../../../../js/enums/InventoryItemStatusTitle';
import {
    OWNER_TYPE_OPTIONS,
    analyzeSerialNumbers,
    batchFieldErrors,
    batchFieldValidation,
    batchReasons,
    batchSerialErrors,
    batchServerTemplateFieldErrors,
    batchTemplateFieldErrors,
    batchUnitErrors,
    CREATION_STATUSES,
    batchUnplacedErrors,
    buildBatchPayload,
    describeBatchErrors,
    ownerSelectionFields,
    plannedDateFields,
    validateBatch,
    warrantyHint,
} from '../../../../js/inventoryTemplate';
import { createInventoryItems } from '../../../../js/inventory-api';
import { errorMessage } from '../../../../js/error';

/**
 * Создание оборудования: одна единица или партия.
 *
 * ОДИН КОМПОНЕНТ, ДВА ПРЕДСТАВЛЕНИЯ (7-D3). Данные у одной единицы и у партии те же
 * самые, и второй формы для них не заводится: две формы одного и того же однажды
 * разошлись бы составом обязательных полей — это уже случалось здесь до 7-D1. Меняется
 * только то, как форма себя показывает: заголовок, подписи разделов, нумерация строк
 * и счётчик заполненных номеров (см. isBatch). Ни одно ПРАВИЛО от количества не зависит.
 *
 * ОДНА ФОРМА ВМЕСТО N КАРТОЧЕК. Раньше «количество» просто размножало одну и ту же
 * карточку: сорок единиц получали один серийный номер на всех, то есть учёт партии
 * был сломан по построению. Здесь данные разделены на общие и индивидуальные, и
 * индивидуальное ровно одно — серийный номер (плюс ответственный, когда технику
 * раздают разным людям).
 *
 * ВСЯ ПРОВЕРЯЕМАЯ ЛОГИКА ЖИВЁТ В js/inventoryTemplate.js: разбор вставленного списка,
 * поиск дублей, расчёт гарантии, сборка запроса. Здесь остаются разметка и состояние —
 * так же устроены вкладка «Файлы» и карточка предмета, и по той же причине: форму
 * в тестах не монтируют, а правила проверять нужно.
 */
@Component({
    components: {
        VButton,
        VSelect,
        VModalWrapper,
    },
})
class VInventoryBatchModal extends ModalMixin {
    @Prop({ type: String, default: ModalNames.INVENTORY_BATCH_CREATE })
    name;
    @Prop({ type: String, default: 'Новое оборудование' })
    defaultTitle;
    @Prop({ type: Object, required: true })
    cache;

    common = null;
    units = [];
    count = 1;

    /**
     * Показывать ли ошибки полей.
     *
     * Свежая форма не красная: ни одно поле ещё не заполняли, и объявлять их все
     * незаполненными значит кричать раньше, чем человек что-то сделал. Проверки
     * появляются по первой попытке сохранить — и дальше держатся, пока форма открыта,
     * чтобы исправление было видно сразу.
     */
    showErrors = false;

    // Ошибки, пришедшие от сервера, разложенные по местам: общие поля отдельно,
    // позиции отдельно, непривязанные — списком внизу. Сырой текст ответа сюда
    // не попадает никогда: у Laravel он приходит ключом языкового файла
    // («validation.required»), и показывать такое человеку нельзя.
    serverFieldErrors = {};
    serverUnitErrors = {};
    serverTemplateFieldErrors = {};
    serverCommonErrors = [];

    isSaving = false;

    // Описание справочника серийных номеров приходит от страницы: названия справочников
    // заводит администратор, и зашивать одно из них в форму нельзя.
    serialAttribute = null;

    /**
     * Партия или одна единица — единственное, от чего зависит представление формы.
     */
    get isBatch() {
        return Number(this.count) > 1;
    }

    get serialSectionTitle() {
        return this.isBatch ? 'Серийные номера' : 'Серийный номер';
    }

    /**
     * Подпись поля номера. В партии она различает строки, у одной единицы — называет поле.
     *
     * @param {number} index
     * @returns {string}
     */
    serialPlaceholder(index) {
        return this.isBatch ? `Серийный номер ${index + 1}` : 'Серийный номер';
    }

    get cityOptions() {
        return this.cache?.cities ?? [];
    }

    get legalEntityOptions() {
        return this.cache?.legalEntities ?? [];
    }

    get userOptions() {
        return this.cache?.users ?? [];
    }

    get holderOptions() {
        return this.cache?.holders ?? [];
    }

    get ownerOptions() {
        return OWNER_TYPE_OPTIONS;
    }

    /**
     * Только статусы заведения. Ремонт, неисправность и списание — действия в карточке
     * с обязательной причиной; «Списано» к тому же необратимо. Сервер отвергнет их так же.
     */
    get statusOptions() {
        return CREATION_STATUSES.map((value) => ({
            value,
            label: InventoryItemStatusTitle[value],
        }));
    }

    /**
     * Поля, пришедшие из шаблона. Правит их человек прямо в форме, поэтому это ссылка
     * на массив внутри common, а не копия.
     */
    get templateFields() {
        return this.common?.fields ?? [];
    }

    /**
     * Ошибки полей шаблона: своя проверка и то, что вернул сервер.
     *
     * Ключ — МЕСТО поля в списке, а не его название: подпись поля задаёт администратор,
     * и два одинаковых названия подсветили бы обе строки вместо одной.
     */
    get templateFieldErrors() {
        const own = this.showErrors ? batchTemplateFieldErrors(this.templateFields) : {};

        return { ...own, ...this.serverTemplateFieldErrors };
    }

    get ownerFields() {
        return ownerSelectionFields(this.common?.owner_type);
    }

    get dateFields() {
        return plannedDateFields(this.common?.status);
    }

    get warrantyLabel() {
        return warrantyHint(this.common?.delivered_at, this.common?.warranty_months);
    }

    get serials() {
        return this.units.map((unit) => unit.serial);
    }

    get serialAnalysis() {
        return analyzeSerialNumbers(this.serials, this.count);
    }

    /**
     * Строка под полем вставки: сколько заполнено, чего не хватает, что лишнее.
     * Три разных беды, и человек исправляет их по-разному — поэтому текст разный.
     */
    get serialSummary() {
        const analysis = this.serialAnalysis;

        if (analysis.missing > 0) {
            return `Заполнено ${analysis.filled} из ${this.count}.`;
        }

        if (analysis.duplicates.length > 0) {
            return 'Есть повторяющиеся номера.';
        }

        return `Все ${this.count} номеров заполнены.`;
    }

    get validation() {
        return validateBatch(this.common ?? {}, this.serials, this.count);
    }

    /**
     * Ошибки общих полей: свои проверки и то, что вернул сервер.
     *
     * Ответ сервера кладётся поверх собственной проверки, а не наоборот: если форма
     * считает поле заполненным, а сервер его отверг, показать надо именно отказ сервера —
     * он последнее слово и знает то, чего форма знать не может (занятый номер, чужой город).
     */
    get fieldErrors() {
        const own = this.showErrors ? batchFieldValidation(this.common ?? {}) : {};

        return { ...own, ...this.serverFieldErrors };
    }

    /**
     * Ошибки позиций: номер позиции → текст.
     *
     * Пустой и повторяющийся номер видны до отправки — их находит сама форма; отказ
     * сервера («номер уже заведён у оборудования № 812») приходит только после неё
     * и встаёт в ту же строку.
     */
    get unitErrors() {
        const own = this.showErrors ? batchSerialErrors(this.serials, this.count) : {};

        // Дубль внутри партии форма видит и до отправки: этот текст полезен сразу,
        // не дожидаясь попытки сохранить.
        const duplicates = {};

        this.serialAnalysis.states.forEach((state, index) => {
            if (state === 'duplicate') {
                duplicates[index] = 'Такой номер уже есть в партии.';
            }
        });

        return { ...own, ...duplicates, ...this.serverUnitErrors };
    }

    /**
     * Подпись кнопки: одна единица — «Создать», партия — «Создать N».
     */
    get createLabel() {
        return this.isBatch ? `Создать ${this.count}` : 'Создать';
    }

    /**
     * То, что не встало ни к одному полю формы.
     *
     * Собственная причина здесь ровно одна — количество единиц: его задаёт предыдущий
     * шаг, и поля для него в форме нет. Остальное — ответы сервера по ключам, которым
     * не нашлось поля: потерять ошибку хуже, чем показать её обобщённо.
     */
    get commonErrors() {
        return batchReasons(this.count).concat(this.serverCommonErrors);
    }

    /**
     * @param {number} index
     * @returns {string}
     */
    serialState(index) {
        return this.serialAnalysis.states[index] ?? 'empty';
    }

    /**
     * Разложить ответ 422 по местам: общие поля, позиции, остальное.
     *
     * СЫРОЙ ТЕКСТ СЕРВЕРА НЕ ПОКАЗЫВАЕТСЯ НИКОГДА. Русского файла валидации в проекте нет,
     * и Laravel отдаёт ключ языкового файла — пользователь видел буквально
     * «validation.required». Текст собирается здесь заново, по именам полей.
     *
     * @param {Object} error Ошибка запроса.
     * @returns {boolean} Удалось ли разобрать ответ как ошибки валидации.
     */
    applyServerErrors(error) {
        const errors = error?.response?.data?.errors;

        if (!errors || typeof errors !== 'object') {
            return false;
        }

        this.serverFieldErrors = batchFieldErrors(errors);
        this.serverUnitErrors = batchUnitErrors(errors);
        this.serverTemplateFieldErrors = batchServerTemplateFieldErrors(errors, this.templateFields);

        // В общий список попадает ТОЛЬКО то, что не встало ни к одному полю. Раньше
        // условие было грубее — «хоть что-то разложилось, значит список пуст», — и
        // вторая, неразложенная ошибка из того же ответа пропадала совсем.
        const unplaced = batchUnplacedErrors(errors, this.templateFields);
        const rest = {};

        unplaced.forEach((key) => {
            rest[key] = errors[key];
        });

        this.serverCommonErrors = unplaced.length > 0 ? describeBatchErrors(rest) : [];

        return true;
    }

    /**
     * Сбросить прежние ошибки сервера: новая попытка начинается с чистого листа.
     */
    clearServerErrors() {
        this.serverFieldErrors = {};
        this.serverUnitErrors = {};
        this.serverTemplateFieldErrors = {};
        this.serverCommonErrors = [];
    }

    /**
     * Создать партию.
     *
     * ЗАПРОС ДЕЛАЕТ ФОРМА, А НЕ СТРАНИЦА, И ЭТО ГЛАВНОЕ ЗДЕСЬ. Раньше форма закрывалась
     * сразу по нажатию, а запрос отправляла страница — и отказ сервера приходить было
     * уже некуда: форма закрыта, поля исчезли, оставалось показать общий текст ошибки
     * поверх списка. Теперь форма остаётся открытой, пока партия не создана: отказ
     * раскладывается по тем самым полям, которые надо исправить, и введённое не теряется.
     */
    async submit() {
        if (this.isSaving) {
            return;
        }

        // Нажатие — это и есть просьба проверить. Незаполненное показывается под своими
        // полями; форма при этом никуда не уходит и ничего не теряет.
        this.showErrors = true;

        if (!this.validation.isValid) {
            return;
        }

        this.clearServerErrors();
        this.isSaving = true;

        try {
            const response = await createInventoryItems(this.payload());

            this.resolve(response.data);
            this.hide();
        } catch (error) {
            // Ошибку валидации показываем в форме; всё остальное (сеть, права, отказ
            // по городу) — общей модалкой: к полю это не привязать, и прятать внизу
            // формы такое значило бы прятать совсем.
            if (!this.applyServerErrors(error)) {
                this.$showModal(ModalNames.ERROR, { errorMessage: errorMessage(error) });
            }
        } finally {
            this.isSaving = false;
        }
    }

    /**
     * Позиции партии для bulk-insert.
     *
     * Юр. лицо выбирается селектом, а хранится значением справочника: превращаем
     * выбранную опцию в атрибут ровно здесь, один раз на всю партию.
     *
     * @returns {Array}
     */
    payload() {
        const common = { ...this.common };
        const legalEntity = this.legalEntityOptions.find((option) => option.id === this.common.legal_entity_id);

        // Юр. лицо выбирается селектом, а хранится значением справочника: добавляем его
        // к полям формы одним полем — дальше всё собирает buildBatchPayload().
        if (legalEntity) {
            common.fields = [
                ...(common.fields ?? []),
                {
                    attribute_id: legalEntity.id,
                    attribute_type: legalEntity.type,
                    type: legalEntity.type,
                    value: legalEntity.name,
                    value_type: 'string',
                    searchable: false,
                },
            ];
        }

        return buildBatchPayload(common, this.units, this.serialAttribute);
    }

    beforeOpen(event) {
        const params = event?.params ?? {};

        this.count = Number(params.count) > 0 ? Number(params.count) : 1;

        // Заголовок называет то, что происходит. «Создание партии» над одним полем
        // серийного номера — обещание партии, которой нет; ModalMixin ставит сюда
        // defaultTitle, и здесь он уточняется по количеству.
        if (!params.title) {
            this.title = this.isBatch ? 'Создание партии' : 'Новое оборудование';
        }

        this.common = {
            legal_entity_id: null,
            holder_expected_at: null,
            holder_until_at: null,
            fields: [],
            ...(params.common ?? {}),
        };

        // Юр. лицо из шаблона подставляется в собственный селект формы: шаблон задал
        // значение, а выбирает из справочника всё равно форма.
        if (!this.common.legal_entity_id && params.common?.legal_entity_default) {
            const option = this.legalEntityOptions.find(
                (candidate) => candidate.name === params.common.legal_entity_default,
            );

            this.common.legal_entity_id = option ? option.id : null;
        }
        this.serialAttribute = params.serialAttribute ?? null;
        this.isSaving = false;
        this.showErrors = false;
        this.clearServerErrors();
        this.units = new Array(this.count).fill(null).map(() => ({ serial: '', responsible_person_id: null }));
    }

    beforeClose() {
        this.common = null;
        this.units = [];
        this.showErrors = false;
        this.clearServerErrors();
    }
}

export default VInventoryBatchModal;
</script>
<style lang="scss">
// Создание партии. СНАЧАЛА УЗКИЙ ЭКРАН: заводят со склада, с телефона в одной руке.
.batch-form {
    &__section {
        margin-bottom: 18px;
    }

    &__section-title {
        margin: 0 0 10px;
        color: #5f6368;
        font-size: 0.75rem;
        font-weight: 500;
        text-transform: uppercase;
        letter-spacing: 0.04em;
    }

    // Одна колонка на телефоне, две и больше — когда есть ширина. Без медиазапроса:
    // ширина модалки зависит не только от окна.
    &__grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
        gap: 12px;
    }

    &__field {
        min-width: 0;
    }

    // Ошибка поля — рядом с полем, а не в общем списке внизу: искать, к чему относится
    // «укажите город», когда полей десять, человек не должен.
    &__error {
        display: block;
        margin-top: 4px;
        color: var(--color-red);
        font-size: 0.75rem;
    }

    &__hint {
        display: block;
        margin-top: 4px;
        color: #5f6368;
        font-size: 0.75rem;
    }

    &__units {
        display: grid;
        gap: 8px;
        margin: 0;
        padding: 0;
        list-style: none;
    }

    // Строка единицы: номер по порядку слева, поле во всю оставшуюся ширину.
    // На телефоне это одна строка, а не ячейка таблицы, которую надо листать вбок.
    &__unit {
        display: flex;
        align-items: flex-start;
        gap: 8px;
    }

    &__unit-number {
        flex: 0 0 auto;
        width: 28px;
        padding-top: 10px;
        color: #9aa0a6;
        font-size: 0.75rem;
        text-align: right;
    }

    &__unit-field {
        flex: 1 1 auto;
        min-width: 0;
    }

    &__serial {
        width: 100%;
        // 40 точек — нижняя граница уверенного попадания пальцем.
        min-height: 40px;
        font-family: 'Roboto Mono', Consolas, monospace;

        &_empty {
            border-color: #f2c94c;
        }

        &_duplicate {
            border-color: var(--color-red);
        }
    }

    &__serial-note {
        display: block;
        margin-top: 2px;
        color: var(--color-red);
        font-size: 0.75rem;
    }

    // Причины, по которым кнопка недоступна. Списком под формой, а не одной общей
    // фразой: «проверьте поля» заставляет искать, что именно не так.
    &__reason {
        margin: 0 0 4px;
        color: var(--color-red);
        font-size: 0.8125rem;
    }

    &__actions {
        display: flex;
        justify-content: flex-end;
        gap: 10px;
        margin-top: 16px;
    }
}
</style>
